import {Component, ViewChild} from '@angular/core';
import {IBaseModalComponent} from '../../../../../sdk/src/components/ibase-modal/ibase-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCTaskExecutionApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task-execution.api-service';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {GenericTableComponent} from '../../../../../sdk/src/components/generic-table/generic-table.component';
import {
    ETableColDataType,
    ETableColFreeze,
    ETableColType,
    TableColDef
} from '../../../../../sdk/src/components/generic-table/table-col-def';
import {TaskExecutionModelExtension} from '../../../api-extensions/models/task-execution.model-extension';
import {TaskExecutionLogsModalComponent} from '../task-execution-log-modal/task-execution-log-modal.component';
import {SDKConfig} from '../../../../../sdk/src/sdk.config';

export class TaskExecutionsModalConfig {
    constructor(
        public taskUUID: string,
        public taskName: string | undefined
    ) {}
}

@Component({
    selector: 'app-task-executions-modal',
    templateUrl: './task-executions-modal.component.html',
    imports: [PrimeNgImportsModule, GenericTableComponent]
})
export class TaskExecutionsModalComponent extends IBaseModalComponent<
    TaskExecutionsModalComponent,
    TaskExecutionsModalConfig
> {
    protected readonly CpmCTaskExecutionApiService = CpmCTaskExecutionApiService;

    // ===============================================================
    // ===============================================================

    protected _contentMinHeight = '';
    protected _taskUUID!: string;
    protected _tableCols: TableColDef[] = [];
    protected _items: TaskExecutionModelExtension[] = [];
    protected _totalItemCount = -1;

    @ViewChild(GenericTableComponent, {static: true})
    protected genericTableComponent!: GenericTableComponent<TaskExecutionModelExtension>;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.config.width = '75%';
        this._contentMinHeight = '20rem';

        this.config.draggable = true;
        this.config.maximizable = true;
        this.config.resizable = true;
        this.config.keepInViewport = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get dialog service parameters
     * @param config modal configuration
     * @returns DynamicDialogConfig<TaskDependsWarningModalConfig>
     */
    public static getDialogServiceParameters(
        config: TaskExecutionsModalConfig
    ): DynamicDialogConfig<TaskExecutionsModalConfig> {
        return {
            modal: true,
            header: `Execution history for: ${config.taskName}`,
            data: config
        };
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this._taskUUID = this.config.data!.taskUUID;
        this.showLoading = true;

        this.loadingStr = 'Initializing...';

        await this.asyncCritical(async () => {
            this.initColumns();
            await this.refresh();
        });

        this.showLoading = false;
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Refresh
     */
    public async refresh(): Promise<void> {
        this.showLoading = true;
        this.showError = false;

        await this.asyncCritical(async () => {
            const result = await firstValueFrom(
                CpmCTaskExecutionApiService.getTaskExecutionList(
                    this._taskUUID,
                    {},
                    this.genericTableComponent.paginatorConfig
                )
            );

            this._items = result.data;
            this._totalItemCount = result.total;

            this._items
                .filter((x) => x.execution_duration)
                .forEach((x) => {
                    x._rounded_execution_duration = Math.ceil(x.execution_duration!) + ' second(s)';
                    x._execution_duration_tooltip = this.generateExecutionDurationTooltip(x.execution_duration!);
                });
        });

        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Generate the execution duration tooltip
     * @param executionDuration
     * @returns Formatted execution duration tooltip
     */
    private generateExecutionDurationTooltip(executionDuration: number): string {
        const total = Math.round(executionDuration);

        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;

        const parts: string[] = [];

        if (hours > 0) {
            parts.push(`${hours} ${hours === 1 ? 'Hour' : 'Hours'}`);
        }
        if (minutes > 0) {
            parts.push(`${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`);
        }
        if (seconds > 0 || parts.length === 0) {
            parts.push(`${seconds} ${seconds === 1 ? 'Second' : 'Seconds'}`);
        }

        return parts.join(' ');
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected initColumns() {
        this._tableCols = [
            {
                field: 'job_uuid',
                header: 'Job UUID',
                dataType: ETableColDataType.TEXT,
                tdStyle: {'vertical-align': 'baseline', 'font-family': 'monospace, monospace'},
                freeze: ETableColFreeze.LEFT
            },
            {
                field: 'success',
                header: 'Success',
                dataType: ETableColDataType.BOOLEAN_ICON,
                tdStyle: {'vertical-align': 'baseline'}
            },
            {
                field: 'created_at',
                header: 'Started at',
                dataType: ETableColDataType.DATE,
                customDateFormat: SDKConfig.dateFormatFull,
                tdStyle: {'vertical-align': 'baseline'}
            },
            {
                field: 'end_date',
                header: 'Ended at',
                dataType: ETableColDataType.DATE,
                customDateFormat: SDKConfig.dateFormatFull,
                tdStyle: {'vertical-align': 'baseline'}
            },
            {
                field: '_rounded_execution_duration',
                tooltipField: '_execution_duration_tooltip',
                header: 'Duration',
                dataType: ETableColDataType.TEXT,
                tdStyle: {'vertical-align': 'baseline'}
            },
            {
                field: 'failure_message',
                header: 'Error',
                dataType: ETableColDataType.TEXT,
                tdStyle: {
                    'vertical-align': 'baseline',
                    'font-family': 'monospace, monospace',
                    'word-wrap': 'break-word',
                    'overflow-wrap': 'break-word',
                    'white-space': 'normal',
                    width: '80%'
                }
            },
            {
                type: ETableColType.BUTTON,
                field: '___LOGS_BTN___',
                header: '',
                icon: 'pi pi-list-check',
                tdStyle: {'vertical-align': 'baseline'},
                buttonSeverity: 'secondary',
                fnOnButtonClick: (row: TaskExecutionModelExtension) => {
                    this.globalsService.dialogService.open(
                        TaskExecutionLogsModalComponent,
                        TaskExecutionLogsModalComponent.getDialogServiceParameters({
                            taskUUID: this._taskUUID,
                            jobUUID: row.job_uuid,
                            taskName: this.config.data?.taskName,
                            jobCreatedAt: row.created_at
                        })
                    );
                }
            }
        ];
    }
}
